import './Nav.css'

import type { ParseKeys } from 'i18next'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation, useNavigate } from 'react-router'

import { Button } from '#design-system/component/Button/Button'
import { MagnifyingGlass } from '#design-system/icons/magnifying-glass'
import { useSignOutHandler } from '~/common/hooks/useSignOutHandler'
import {
  SHELL_TEST_NAV_APP_TREE,
  SHELL_TEST_NAV_FLYOUT,
  SHELL_TEST_NAV_SECTIONS,
} from '~/test-data/shell'

import { AppNav } from './AppNav'
import { ModeSwitcher } from './ModeSwitcher'
import { SearchDialog } from './SearchDialog'
import { SettingsNav } from './SettingsNav'
import { TenantSwitcher } from './TenantSwitcher'

export interface NavChildItem {
  path: string
  labelKey: ParseKeys
}

export interface NavSectionConfig {
  path: string
  section: string
  labelKey: ParseKeys
  children: NavChildItem[]
}

// Stores the last visited route per section so navigation back to a section resumes where the
// user left off rather than always returning to the section root.
const _lastRoute: Record<string, string> = {}
export const setLastRoute = (section: string, route: string): void => {
  _lastRoute[section] = route
}
const getLastRoute = (section: string, fallback: string): string => _lastRoute[section] ?? fallback

/**
 * A section is active when the current location is its route or any descendant.
 * The index route (`/`) renders Home, which has no path of its own to match against.
 */
const isSectionActive = (section: NavSectionConfig, pathname: string): boolean =>
  matchPath({ path: section.path, end: false }, pathname) !== null ||
  (pathname === '/' && section.path === '/home')

export type NavState = 'expanded' | 'collapsed' | 'peek'

interface NavProps {
  navState?: NavState
}

export const Nav = ({ navState = 'expanded' }: NavProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isSettings = matchPath({ path: '/settings', end: false }, pathname) !== null
  const handleSignOut = useSignOutHandler()
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  // Toggle search panel on ⌘K (Mac) / Ctrl+K (Windows/Linux).
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
    }
  }, [])

  return (
    <nav
      aria-hidden={navState === 'collapsed' || undefined}
      className={`nav ${navState}`}
      id="shell-nav"
      inert={navState === 'collapsed' || undefined}
    >
      <TenantSwitcher
        member={SHELL_TEST_NAV_FLYOUT.member}
        memberCount={SHELL_TEST_NAV_FLYOUT.memberCount}
        onInviteMembers={() => undefined}
        onLogOut={() => {
          void handleSignOut()
        }}
        onSettings={() => {
          void navigate('/settings')
        }}
        onSwitchWorkspace={() => undefined}
        tenant={SHELL_TEST_NAV_FLYOUT.tenant}
        workspaceTree={SHELL_TEST_NAV_FLYOUT.workspaceTree}
      />

      <button
        className="item"
        onClick={() => {
          setIsSearchOpen(true)
        }}
        type="button"
      >
        <MagnifyingGlass className="icon" />
        <span className="label">{t('shell.nav.search')}</span>
        <kbd aria-hidden="true">{t('keyboardShortcuts.search')}</kbd>
      </button>
      <SearchDialog
        isOpen={isSearchOpen}
        onClose={() => {
          setIsSearchOpen(false)
        }}
      />

      {isSettings ? (
        <SettingsNav
          onBackToApps={() => {
            void navigate(getLastRoute('home', '/home'))
          }}
        />
      ) : (
        <>
          <ModeSwitcher
            notificationCount={SHELL_TEST_NAV_FLYOUT.notificationCount}
            onNavigate={(to) => {
              void navigate(getLastRoute(to.slice(1), to))
            }}
          />

          <div className="active-item-links">
            {SHELL_TEST_NAV_SECTIONS.map((section) => {
              if (!isSectionActive(section, pathname)) {
                return null
              }
              // The Home tab renders the nestable app → page → sub-page tree.
              if (section.section === 'home') {
                return <AppNav key={section.section} tree={SHELL_TEST_NAV_APP_TREE} />
              }
              if (section.children.length === 0) {
                return null
              }
              return section.children.map((child) => (
                <Button
                  key={child.path}
                  actionRole="ghost"
                  label={t(child.labelKey)}
                  tagType="NavLink"
                  to={child.path}
                />
              ))
            })}
          </div>
        </>
      )}
    </nav>
  )
}
